import { useEffect } from "react";
import { Download, FolderLock, Image, Loader2, X } from "lucide-react";
import { ChatImageZoom } from "./ChatImageZoom";

type Props = {
  src: string;
  alt?: string;
  /** avatar = aperçu rond (photo de profil) — pas d’actions sauver */
  variant?: "image" | "avatar";
  saveBusy?: boolean;
  onClose: () => void;
  onDownload?: () => void;
  onSaveCoffre?: () => void;
  onSavePhotos?: () => void;
};

export function ImageLightbox({
  src,
  alt = "",
  variant = "image",
  saveBusy = false,
  onClose,
  onDownload,
  onSaveCoffre,
  onSavePhotos,
}: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const isAvatar = variant === "avatar";
  const showSave = !isAvatar && (onDownload || onSaveCoffre || onSavePhotos);

  return (
    <div
      className="chat-lightbox fixed inset-0 z-[90] flex flex-col items-center justify-center gap-3 bg-black/85 p-3 pb-[5.5rem] sm:p-6 sm:pb-6"
      role="dialog"
      aria-modal="true"
      aria-label={isAvatar ? "Photo de profil" : "Aperçu de l’image"}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        className="absolute right-3 top-3 z-[1] rounded-full bg-white/10 p-2 text-white backdrop-blur-sm hover:bg-white/20 sm:right-5 sm:top-5"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Fermer"
      >
        <X className="size-5" />
      </button>
      {isAvatar ? (
        <img
          src={src}
          alt={alt}
          className="chat-lightbox-img size-[min(78vw,22rem)] max-h-[min(70dvh,22rem)] rounded-full object-cover shadow-2xl ring-2 ring-white/20"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />
      ) : (
        <ChatImageZoom src={src} alt={alt} />
      )}
      {isAvatar && alt ? (
        <p
          className="max-w-[min(90vw,22rem)] truncate text-center text-sm font-medium text-white/90"
          onClick={(e) => e.stopPropagation()}
        >
          {alt}
        </p>
      ) : null}
      {showSave ? (
        <div
          className="chat-lightbox-actions pointer-events-auto absolute inset-x-0 bottom-0 z-[2] flex flex-wrap items-center justify-center gap-2 px-3 pb-[max(0.85rem,env(safe-area-inset-bottom))] pt-2"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {onDownload ? (
            <button
              type="button"
              disabled={saveBusy}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-white/12 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/20 disabled:opacity-50"
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
            >
              {saveBusy ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
              Télécharger
            </button>
          ) : null}
          {onSaveCoffre ? (
            <button
              type="button"
              disabled={saveBusy}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-white/12 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/20 disabled:opacity-50"
              onClick={(e) => {
                e.stopPropagation();
                onSaveCoffre();
              }}
            >
              <FolderLock className="size-3.5" />
              Coffre
            </button>
          ) : null}
          {onSavePhotos ? (
            <button
              type="button"
              disabled={saveBusy}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-white/12 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/20 disabled:opacity-50"
              onClick={(e) => {
                e.stopPropagation();
                onSavePhotos();
              }}
            >
              <Image className="size-3.5" />
              Photos
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
